import React, { PropsWithChildren, ReactNode } from 'react';

import { useSortable } from '@dnd-kit/sortable';

import { Box } from '@vk-hr-tek/ui/Box';
import { Icon } from '@vk-hr-tek/ui/Icon';
import { Button } from '@vk-hr-tek/ui/Button';
import { Tooltip } from '@vk-hr-tek/ui/Tooltip';
import { Typography } from '@vk-hr-tek/ui/Typography';

import { ContentSurveysFormAnswerTypes } from '../../../../types';

import { DnDInsertFallback } from './DnDInsertFallback';

interface AnswerContainerProps {
  id: string;
  onRemove: VoidFunction;
  dndFallback?: ReactNode;
  removeActionDisabled?: boolean;
  type: ContentSurveysFormAnswerTypes;
}

export const AnswerContainer = ({
  id,
  type,
  children,
  onRemove,
  dndFallback,
  removeActionDisabled = false,
}: PropsWithChildren<AnswerContainerProps>) => {
  const {
    isOver,
    overIndex,
    listeners,
    setNodeRef,
    attributes,
    isDragging,
    activeIndex,
  } = useSortable({ id });

  const tooltipContent = `При типе ответа «${
    type === 'one' ? 'Один' : 'Несколько'
  } из списка» должно быть добавлено не менее двух ответов. При добавлении следующего ответа удаление станет доступным`;

  if (isDragging && dndFallback) return dndFallback;

  return (
    <>
      {isOver && overIndex < activeIndex && <DnDInsertFallback />}

      <Box
        p="16"
        gap="16"
        radius="l"
        border={1}
        display="flex"
        ref={setNodeRef}
        flexDirection="column"
        borderColor="stroke.primary"
        bgcolor="bg.light.primary"
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Вариант ответа</Typography>

          <Box display="flex" gap="16" alignItems="center">
            <Tooltip
              placement="top"
              title={removeActionDisabled ? tooltipContent : ''}
            >
              <Box cursor="default">
                <Button
                  size="small"
                  onClick={onRemove}
                  variant="tertiaryLight"
                  disabled={removeActionDisabled}
                  icon={
                    <Icon
                      size="24"
                      name="delete"
                      color="original.brand.primary"
                    />
                  }
                />
              </Box>
            </Tooltip>

            <Button
              variant="tertiaryLight"
              {...attributes}
              {...listeners}
              icon={
                <Icon
                  size="24"
                  name="dots_vertical"
                  color="text.light.secondary"
                />
              }
            />
          </Box>
        </Box>

        {children}
      </Box>

      {isOver && overIndex > activeIndex && <DnDInsertFallback />}
    </>
  );
};
